import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines, LineDraw } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

export const CHAPTERS = [
  {
    n: "01", title: "Sales", lead: "The showroom sells you the car.",
    text: "Every JETOUR model on the floor, with people who know the difference between the T2 i-DM and the X70 Plus because they drive them. Transparent ex-factory pricing, booking assistance and delivery updates you never have to chase.",
    image: "/showroom.jpg", caption: "Showroom · West Canal Road",
  },
  {
    n: "02", title: "Service", lead: "The workshop maintains it.",
    text: "A full workshop under the same roof, with JETOUR-specified diagnostic tools and factory-trained technicians. Periodic maintenance, warranty work and repairs, without ever taking your car somewhere else.",
    image: "/workshop.jpg", caption: "Workshop · Service bays",
  },
  {
    n: "03", title: "Spare Parts", lead: "The parts counter keeps it running.",
    text: "Counterfeit parts are a real risk with any newer brand in Pakistan. Every part we fit comes through JETOUR's own supply chain, so you always know exactly what is going into your car, and your warranty stays intact.",
    image: "/spare-parts.jpg", caption: "Genuine JETOUR parts, held in stock",
  },
];

const Chapter = ({ c, flip }) => (
  <div data-testid={`manifesto-chapter-${c.n}`} className="grid items-center gap-8 md:grid-cols-12 md:gap-10">
    <Reveal className={cn("md:col-span-7", flip && "md:order-2")} y={30}>
      <div className="flex items-start gap-6">
        <span className="outline-text font-display text-6xl font-black leading-none sm:text-8xl">{c.n}</span>
        <div className="pt-2">
          <LineDraw className="mb-5 w-12" />
          <h3 className="font-display text-3xl font-bold uppercase tracking-[-0.02em] sm:text-4xl">{c.title}</h3>
          <p className="mt-5 font-editorial text-xl italic leading-snug text-gold sm:text-2xl">{c.lead}</p>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">{c.text}</p>
        </div>
      </div>
    </Reveal>
    <Reveal className={cn("md:col-span-5", flip && "md:order-1")} y={50} delay={0.15}>
      <div className={cn("group relative overflow-hidden aspect-[4/5]", flip ? "clip-frame-alt" : "clip-frame")}>
        <img src={c.image} alt={c.title} loading="lazy" className="h-full w-full object-cover grayscale-[30%] transition-[transform,filter] duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105 group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
        <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">{c.caption}</p>
      </div>
    </Reveal>
  </div>
);

export const Manifesto = ({ settings = {}, compact = false, number = "02" }) => (
  <section id="about" data-testid="manifesto-section" className="relative border-t border-white/10 py-24 sm:py-32">
    <div className="wrap grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-32">
          <Reveal y={16}>
            <p className="eyebrow flex items-center gap-4"><span className="text-crimson">{number}</span><LineDraw className="w-10" />What 3S means</p>
          </Reveal>
          <MaskedLines as="h2" inView lines={["Sold here.", "Serviced here.", "Supplied here."]} className="display-lg mt-6" lineClassName={(i) => (i === 2 ? "font-editorial italic font-normal normal-case tracking-normal text-gold" : "")} />
          {!compact && (
            <>
              <Reveal delay={0.3} className="mt-8 max-w-sm">
                <p className="text-base leading-relaxed text-slate-400">{settings.about_text?.split(". ").slice(0, 3).join(". ")}.</p>
              </Reveal>
              <Reveal delay={0.4} className="mt-8">
                <Link to="/about" data-testid="manifesto-about-link" className="group inline-flex items-center gap-3 font-display text-sm font-semibold uppercase tracking-[0.12em]">
                  More about us
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-[background-color,border-color] duration-300 group-hover:border-crimson group-hover:bg-crimson"><ArrowUpRight className="h-4 w-4" /></span>
                </Link>
              </Reveal>
            </>
          )}
        </div>
      </div>
      <div className="space-y-24 lg:col-span-8 lg:space-y-32">
        {CHAPTERS.map((c, i) => <Chapter key={c.n} c={c} flip={i % 2 === 1} />)}
      </div>
    </div>
  </section>
);
